import { useEffect, useId, useMemo, useRef, useState } from 'react'

import { useScheduleI18n } from '../../i18n'
import { dsText, fontSize, shadowVar, tokenVar } from '../../utils'
import { minutesToLabel } from './weeklyHoursTableConstants'
import { ChevronDownIcon } from './WeeklyHoursTableIcons'

export interface TimeSelectProps {
  value: number | null
  options: number[]
  disabled?: boolean
  placeholder: string
  ariaLabel: string
  onChange: (minutes: number) => void
}

const normalizeTimeQuery = (value: string): string =>
  value.trim().toLowerCase().replace(/\s+/g, '')

export const TimeSelect = ({
  value,
  options,
  disabled = false,
  placeholder,
  ariaLabel,
  onChange,
}: TimeSelectProps) => {
  const { text } = useScheduleI18n()
  const listboxId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQueryState] = useState<string | null>(null)
  // Mirrors `query` synchronously so Tab and the blur that follows it can't
  // both commit the same typed time.
  const queryRef = useRef<string | null>(null)
  const setQuery = (nextQuery: string | null) => {
    queryRef.current = nextQuery
    setQueryState(nextQuery)
  }
  const [activeIndex, setActiveIndex] = useState(-1)

  const filteredOptions = useMemo(() => {
    const normalizedQuery = normalizeTimeQuery(query ?? '')
    if (!normalizedQuery) {
      return options
    }

    return options.filter((minutes) =>
      normalizeTimeQuery(minutesToLabel(minutes)).includes(normalizedQuery),
    )
  }, [options, query])

  const getOptionId = (index: number) => `${listboxId}-option-${index}`
  const hasOptions = filteredOptions.length > 0
  const activeOptionId =
    isOpen && activeIndex >= 0 && activeIndex < filteredOptions.length
      ? getOptionId(activeIndex)
      : undefined

  useEffect(() => {
    if (!activeOptionId) return
    document
      .getElementById(activeOptionId)
      ?.scrollIntoView?.({ block: 'nearest' })
  }, [activeOptionId])

  // Dialogs and drawers listen for Escape on `document` in the capture phase,
  // so it has to be intercepted on `window` before it reaches them.
  useEffect(() => {
    if (!isOpen) return
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.target !== inputRef.current) return
      event.preventDefault()
      event.stopPropagation()
      queryRef.current = null
      setIsOpen(false)
      setQueryState(null)
      setActiveIndex(-1)
    }
    window.addEventListener('keydown', handleEscape, true)
    return () => window.removeEventListener('keydown', handleEscape, true)
  }, [isOpen])

  const openOptions = (direction: 1 | -1 = 1) => {
    if (disabled) return
    const selectedIndex = value === null ? -1 : options.indexOf(value)
    const fallbackIndex = direction === 1 ? 0 : options.length - 1
    setQuery(null)
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : fallbackIndex)
    setIsOpen(true)
    inputRef.current?.select()
  }

  const closeOptions = () => {
    setIsOpen(false)
    setQuery(null)
    setActiveIndex(-1)
  }

  const commitExactQueryMatch = () => {
    const typedQuery = normalizeTimeQuery(queryRef.current ?? '')
    if (!typedQuery) return
    const match = options.find(
      (minutes) => normalizeTimeQuery(minutesToLabel(minutes)) === typedQuery,
    )
    if (match !== undefined && match !== value) {
      onChange(match)
    }
  }

  const selectOption = (minutes: number) => {
    onChange(minutes)
    closeOptions()
    inputRef.current?.focus()
  }

  const moveActiveOption = (direction: 1 | -1) => {
    if (!hasOptions) return
    setActiveIndex((currentIndex) => {
      if (currentIndex < 0) {
        return direction === 1 ? 0 : filteredOptions.length - 1
      }
      return (
        (currentIndex + direction + filteredOptions.length) %
        filteredOptions.length
      )
    })
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault()
        const direction = event.key === 'ArrowDown' ? 1 : -1
        if (event.altKey && direction === -1) {
          if (isOpen) closeOptions()
          return
        }
        if (!isOpen) {
          openOptions(direction)
        } else if (!event.altKey) {
          moveActiveOption(direction)
        }
        return
      }
      case 'Enter': {
        event.preventDefault()
        if (!isOpen) {
          openOptions()
          return
        }
        const activeOption = filteredOptions[activeIndex]
        if (activeOption !== undefined) {
          selectOption(activeOption)
        }
        return
      }
      case 'Home':
      case 'End': {
        if (!isOpen || !hasOptions || queryRef.current !== null) return
        event.preventDefault()
        setActiveIndex(event.key === 'Home' ? 0 : filteredOptions.length - 1)
        return
      }
      case 'Tab': {
        commitExactQueryMatch()
        if (isOpen) closeOptions()
        return
      }
    }
  }

  const noTimesFoundText = text(
    'schedule.weeklyHours.timeSelect.noTimesFound',
    'No times found',
  )

  const textStyles = {
    fontFamily: dsText.Body3.fontFamily,
    fontSize: fontSize(dsText.Body3.fontSize),
    fontWeight: dsText.Body3.fontWeight,
    lineHeight: dsText.Body3.lineHeight,
  }

  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-flex',
        width: '113px',
      }}
    >
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        autoComplete="off"
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls={isOpen && hasOptions ? listboxId : undefined}
        aria-activedescendant={activeOptionId}
        disabled={disabled}
        placeholder={placeholder}
        value={query ?? (value === null ? '' : minutesToLabel(value))}
        onChange={(event) => {
          setQuery(event.target.value)
          setActiveIndex(0)
          setIsOpen(true)
        }}
        onClick={() => {
          if (isOpen) {
            commitExactQueryMatch()
            closeOptions()
          } else {
            openOptions()
          }
        }}
        onFocus={(event) => event.currentTarget.select()}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          commitExactQueryMatch()
          closeOptions()
        }}
        style={{
          width: '100%',
          height: '40px',
          padding: '8px 32px 8px 12px',
          borderRadius: '8px',
          outline: `1px solid ${tokenVar('border.default')}`,
          outlineOffset: '-1px',
          border: 'none',
          backgroundColor: tokenVar('bg.default'),
          color: tokenVar('fg.muted'),
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontFamily: dsText.Body2.fontFamily,
          fontSize: fontSize(dsText.Body2.fontSize),
          fontWeight: dsText.Body2.fontWeight,
          lineHeight: dsText.Body2.lineHeight,
        }}
      />
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: '12px',
          display: 'inline-flex',
          alignItems: 'center',
          pointerEvents: 'none',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <ChevronDownIcon color={tokenVar('fg.default')} />
      </span>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '44px',
            left: 0,
            width: '100%',
            minWidth: '160px',
            backgroundColor: tokenVar('bg.default'),
            border: `1px solid ${tokenVar('border.default')}`,
            borderRadius: '8px',
            boxShadow: shadowVar('default'),
            zIndex: 20,
            overflow: 'hidden',
          }}
        >
          {hasOptions ? (
            <div
              id={listboxId}
              role="listbox"
              aria-label={ariaLabel}
              style={{ maxHeight: '180px', overflowY: 'auto' }}
            >
              {filteredOptions.map((option, index) => {
                const isActive = index === activeIndex
                const isSelected = option === value
                return (
                  <div
                    key={option}
                    id={getOptionId(index)}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(event) => {
                      event.preventDefault()
                      selectOption(option)
                    }}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      color: tokenVar('fg.default'),
                      backgroundColor:
                        isActive || isSelected
                          ? tokenVar('blue.subtle')
                          : 'transparent',
                      ...textStyles,
                      fontWeight: isSelected
                        ? dsText.Body3_Bold.fontWeight
                        : dsText.Body3.fontWeight,
                    }}
                  >
                    {minutesToLabel(option)}
                  </div>
                )
              })}
            </div>
          ) : (
            <div
              aria-hidden="true"
              style={{
                padding: '10px 12px',
                color: tokenVar('fg.subtle'),
                ...textStyles,
              }}
            >
              {noTimesFoundText}
            </div>
          )}
        </div>
      )}
      <div
        role="status"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
        }}
      >
        {isOpen && !hasOptions ? noTimesFoundText : ''}
      </div>
    </div>
  )
}
